import {
    ConfigFieldType,
    FieldStatus,
    SelectorFieldDataImpl,
    SelectorOption,
    SelectorType,
    SelectorValueOptions,
} from 'adapter/in/common/form/types';
import { buildSelectorFieldData } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import i18n from 'asset/translation';
import PresetFormFieldProperties from 'domain/entity/preset/form/presetFormFieldProperties';
import { FieldError } from 'react-hook-form';

export type SelectorContainerProps = {
    name: string;
    label: string;
    configType: ConfigFieldType;
    pageSize?: number;
    affectsTo?: string[];
    dataDependsOn?: string[];
    properties?: PresetFormFieldProperties;
    disabled?: boolean;
    footer?: string;
};

export const mapSelectedValues = (values: string[]): SelectorValueOptions =>
    values.map((value: string) => ({
        id: value,
        type: SelectorType.SELECT,
        value,
        label: '',
        color: 'gray300',
    }));

export const isSelectorValueValid = (minSelectionLength: number, maxSelectionLength: number) => (value: SelectorValueOptions) =>
    value.length >= minSelectionLength && value.length <= maxSelectionLength;

export const getErrorMessage = (value: SelectorValueOptions, minSelectionLength: number, maxSelectionLength: number) => {
    if ((value && value.length < minSelectionLength && minSelectionLength === 1) || (value.length === 1 && value[0] === null)) {
        return i18n.t('widgets.errorSelectLeastOne');
    }
    if ((value && value.length < minSelectionLength && minSelectionLength > 1) || (value.length === 1 && value[0] === null)) {
        return i18n.t('widgets.errorSelectMin', { min: minSelectionLength });
    }
    if (value && value.length > maxSelectionLength) return i18n.t('widgets.errorSelectMaxOrFewer', { max: maxSelectionLength });
    return '';
};

export const onChangeSelect = (
    minSelectionLength: number,
    maxSelectionLength: number,
    configType: ConfigFieldType,
    value: SelectorFieldDataImpl
) => (sValue: string | string[], onChangeCallback: (e: SelectorFieldDataImpl) => void) => {
    let parsedValue: SelectorOption[] = [];
    if (value !== undefined) parsedValue = mapSelectedValues(Array.isArray(sValue) ? sValue : [sValue]);
    onChangeCallback(
        buildSelectorFieldData(
            isSelectorValueValid(minSelectionLength, maxSelectionLength)(parsedValue) ? FieldStatus.HAS_VALUE : FieldStatus.INVALID,
            parsedValue.map((selValue) => selValue.value).join(','),
            configType,
            parsedValue
        )
    );
};

export const onChangeSelectAll = (configType: ConfigFieldType) => (values: string[], onChange: (e: SelectorFieldDataImpl) => void) => {
    const parsedSelectedValues = values !== undefined ? mapSelectedValues(values) : [];
    onChange(
        buildSelectorFieldData(
            FieldStatus.HAS_VALUE,
            parsedSelectedValues.map((selValue) => selValue.value).join(','),
            configType,
            parsedSelectedValues
        )
    );
};

export const hasErrorFormSelector = (invalid: boolean, error?: FieldError, options?: SelectorOption[]) =>
    invalid && !!error && error.type !== 'loading' && options !== undefined && options.length > 0;
